import { useState } from 'react'
import { useQuery } from 'react-query'
import {
  downloadArquivo,
  IconPaths,
  ModalSize,
  showError,
  toAbsoluteUrl,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  InfoAlert,
  LoadingIndicator,
  PageHeading,
  useModal,
} from '../../../../../../components'
import {
  downloadProntuario,
  getAvatarPaciente,
} from '../../../../paciente-management/pacientes-list/core/_requests'
import { usePacienteHistorico } from '../../core/usePacienteHistorico'
import { AvaliacoesSection } from '../sections/AvaliacoesSection'
import { PrescricoesSection } from '../sections/PrescricoesSection'

const AVATAR_PADRAO = '/media/avatars/AVATARES_05.png'

/**
 * Cabeçalho do histórico clínico, acima dos cards: nome do paciente como título; prontuário, nascimento, sexo
 * e instituição como apoio. Alergias ficam à vista (informação de segurança clínica).
 */
export const PacienteHistoricoHeading = () => {
  const { paciente, isError, isMedico, pacienteId } = usePacienteHistorico()
  const { showModal } = useModal()
  const [baixando, setBaixando] = useState(false)

  // Sem foto cadastrada (ou falha ao buscar), fica o avatar padrão — sem alerta de erro.
  const { data: avatar } = useQuery(
    ['historico-paciente-avatar', pacienteId],
    () => getAvatarPaciente(pacienteId),
    { enabled: Boolean(pacienteId), refetchOnWindowFocus: false, retry: false }
  )

  const baixarProntuario = async () => {
    try {
      setBaixando(true)
      const anexo = await downloadProntuario(pacienteId)
      downloadArquivo(anexo)
    } catch (error) {
      showError(error)
    } finally {
      setBaixando(false)
    }
  }

  const doPaciente = paciente?.nome ? ` de ${paciente.nome}` : ''

  const abrirAvaliacoes = () =>
    showModal({
      title: `Avaliações${doPaciente}`,
      size: ModalSize.Large,
      content: <AvaliacoesSection pacienteId={pacienteId} />,
    })

  const abrirPrescricoes = () =>
    showModal({
      title: `Prescrições${doPaciente}`,
      size: ModalSize.Large,
      content: <PrescricoesSection pacienteId={pacienteId} />,
    })

  const titulo = paciente?.nome ?? (isError ? 'Paciente' : 'Carregando paciente…')
  const descricao = [
    paciente?.prontuario && `Prontuário ${paciente.prontuario}`,
    paciente?.pessoa?.dataNascimentoFormatada,
    paciente?.pessoa?.sexo,
    paciente?.empresa?.nomeFantasia,
  ]
    .filter(Boolean)
    .join(' · ')
  const alergias = paciente?.alergias ?? []

  return (
    <>
      <PageHeading
        title={titulo}
        description={descricao || undefined}
        avatar={<img src={toAbsoluteUrl(avatar ?? AVATAR_PADRAO)} alt='' />}
        // Documentos do paciente ficam a um clique (modal), fora da página: o foco da tela é a
        // lista de atendimentos. Prescrições e prontuário só para médico da instituição.
        actions={
          <div className='d-flex flex-wrap gap-2'>
            <Button variant='secondary-outline' icon={IconPaths.NewFile} onClick={abrirAvaliacoes}>
              Avaliações
            </Button>
            {isMedico && (
              <Button variant='secondary-outline' icon={IconPaths.Notas} onClick={abrirPrescricoes}>
                Prescrições
              </Button>
            )}
            {isMedico && (
              <Button
                className='btn-primary'
                icon={baixando ? undefined : IconPaths.CloudDownload}
                onClick={baixarProntuario}
                disabled={baixando}
                ariaLabel={baixando ? 'Baixando prontuário…' : undefined}
              >
                {baixando ? <LoadingIndicator /> : 'Baixar prontuário'}
              </Button>
            )}
          </div>
        }
      />
      {alergias.length > 0 && (
        <InfoAlert variant='warning' title='Alergias'>
          {alergias.map((alergia) => alergia.descricao).join(', ')}
        </InfoAlert>
      )}
    </>
  )
}
